import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/primitives/Badge';
import { Zap, Lock, Calendar, TrendingUp } from 'lucide-react';
import { AppStoreBadges } from '@/components/marketing/AppStoreBadges';
import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';
import { SEO, SEO_CONFIG } from '@/components/primitives/SEO';
import { calculateAllStormPassPricing, type StormPassPricing } from '@/lib/pricing';
import { canadianCities } from '@/data/cities';

export function StormPassPage() {
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [drivewaySize, setDrivewaySize] = useState<'small' | 'medium' | 'large'>('medium');

  // Calculate pricing for all cities
  const allPricing = useMemo(() => {
    return calculateAllStormPassPricing(drivewaySize);
  }, [drivewaySize]);

  // Get top 3 cities for featured display
  const featuredCities = useMemo(() => {
    const featured = ['ottawa', 'montreal', 'toronto'];
    return featured.map(slug => allPricing.find(p => p.citySlug === slug)).filter((p): p is StormPassPricing => !!p);
  }, [allPricing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would call the API
    console.log('Waitlist signup:', { email, city });
    setSubmitted(true);
  };

  return (
    <>
      <SEO {...SEO_CONFIG.stormPass} />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden py-20">
        {/* Animated Background Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/30 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative container-default mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="accent" className="mb-6 bg-accent-500/20 border border-accent-500/30 text-accent-500">
              Limited Availability
            </Badge>
          </motion.div>

          <motion.h1 
            className="text-hero mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Storm Pass:<br />
            <span className="gradient-text">Never think about snow again</span>
          </motion.h1>

          <motion.p 
            className="text-body-lg text-snow-100 mb-10 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <strong className="font-semibold text-white">Auto-clear your driveway every storm.</strong> Priority matching. 
            Locked-in pricing. <span className="text-accent-500 font-semibold">The ultimate winter peace-of-mind subscription.</span>
          </motion.p>
        </div>
      </section>

      {/* What is Storm Pass */}
      <Section
        eyebrow="What is Storm Pass?"
        title="Your personal snow removal autopilot"
        subtitle="Set your snow depth threshold and we'll auto-book a clear every time it hits. No manual booking. No surge pricing. Just wake up to a cleared driveway."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {[
            {
              icon: <Zap className="w-8 h-8" />,
              title: 'Auto-booking',
              desc: 'We monitor weather and book automatically when snow hits your threshold',
            },
            {
              icon: <Lock className="w-8 h-8" />,
              title: 'No surge pricing',
              desc: 'Locked-in rate for the season—even during blizzards',
            },
            {
              icon: <Calendar className="w-8 h-8" />,
              title: 'Priority matching',
              desc: 'Jump the queue and get matched with operators faster',
            },
            {
              icon: <TrendingUp className="w-8 h-8" />,
              title: 'Save 10-30%',
              desc: 'Season-long savings vs pay-per-job pricing',
            },
          ].map((feature, i) => (
            <Card key={i} variant="outline" icon={feature.icon} className="hover:border-accent-500 transition-all">
              <h3 className="font-bold mb-2">{feature.title}</h3>
              <p className="text-sm text-neutral-700">{feature.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section
        eyebrow="How it works"
        title="Three steps to never shovel again"
        background="frost"
      >
        <div className="max-w-3xl mx-auto mt-12 space-y-8">
          <div className="flex items-start gap-6">
            <div className="w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center text-2xl font-bold flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Choose your threshold</h3>
              <p className="text-neutral-700">
                Set your preferred snow depth (e.g., "clear when 5cm+ falls"). Add time windows like "clear by 7am."
              </p>
            </div>
          </div>

          <div className="flex items-start gap-6">
            <div className="w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center text-2xl font-bold flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">We watch the weather</h3>
              <p className="text-neutral-700">
                Our system monitors real-time weather data at your address. When your threshold hits, we auto-book a clear.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-6">
            <div className="w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center text-2xl font-bold flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Wake up to a clear driveway</h3>
              <p className="text-neutral-700">
                Get notified when the job is matched and completed. Approve the before/after photos and you're done.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Pricing */}
      <Section
        eyebrow="Pricing"
        title="One price. All winter. Zero stress."
        subtitle="Pay upfront, save all season. No per-job charges, no surge, no surprises."
      >
        {/* Driveway Size Selector */}
        <div className="flex justify-center gap-4 mb-8">
          {(['small', 'medium', 'large'] as const).map((size) => (
            <button
              key={size}
              onClick={() => setDrivewaySize(size)}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${
                drivewaySize === size
                  ? 'bg-brand-500 text-white shadow-lg scale-105'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {size === 'small' ? 'Small (1 car)' : size === 'medium' ? 'Medium (2 cars)' : 'Large (3+ cars)'}
            </button>
          ))}
        </div>

        {/* Featured Cities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 max-w-5xl mx-auto">
          {featuredCities.map((pricing, i) => (
            <Card
              key={pricing.citySlug}
              variant={i === 1 ? 'outline' : 'solid'}
              className={i === 1 ? 'border-2 border-accent-500 relative' : ''}
            >
              {i === 1 && (
                <Badge variant="accent" className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  Most popular
                </Badge>
              )}
              <h3 className="text-2xl font-bold mb-2">{pricing.cityName}</h3>
              <div className="text-4xl font-extrabold text-brand-500 mb-4">
                ${pricing.seasonPrice}
                <span className="text-base text-neutral-600 font-normal">/season</span>
              </div>
              <p className="text-neutral-700 mb-2">Avg {pricing.avgClearsPerSeason}</p>
              <p className="text-accent-500 font-semibold mb-6">Save ${pricing.savings} ({pricing.savingsPercent}% off)</p>
              <Button
                variant={i === 1 ? 'primary' : 'secondary'}
                className="w-full"
              >
                Get Storm Pass
              </Button>
            </Card>
          ))}
        </div>

        {/* All Cities Pricing Table */}
        <div className="mt-16 max-w-6xl mx-auto">
          <h3 className="text-2xl font-black text-center mb-8">Storm Pass Pricing - All Cities</h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-500 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-black">City</th>
                    <th className="px-6 py-4 text-right font-black">Season Price</th>
                    <th className="px-6 py-4 text-right font-black">Avg Clears</th>
                    <th className="px-6 py-4 text-right font-black">Per Clear</th>
                    <th className="px-6 py-4 text-right font-black">Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {allPricing.map((pricing, i) => (
                    <tr key={pricing.citySlug} className={i % 2 === 0 ? 'bg-neutral-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-bold text-neutral-900">{pricing.cityName}</td>
                      <td className="px-6 py-4 text-right text-2xl font-black text-brand-500">${pricing.seasonPrice}</td>
                      <td className="px-6 py-4 text-right text-neutral-700">{pricing.avgClearsPerSeason}</td>
                      <td className="px-6 py-4 text-right text-neutral-600">${pricing.avgPerClearCost}</td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-accent-600 font-bold">${pricing.savings}</span>
                        <span className="text-neutral-500 text-sm ml-1">({pricing.savingsPercent}%)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-center text-sm text-neutral-600 mt-4 italic">
            * Pricing based on {drivewaySize} driveway size. Historical weather data from Environment Canada.
          </p>
        </div>
        
        <div className="mt-16 text-center">
          <p className="text-body-lg text-neutral-700 mb-8 max-w-2xl mx-auto">
            Download the SNOWBLOWR app to activate Storm Pass in your city
          </p>
          <AppStoreBadges />
        </div>
      </Section>

      {/* Waitlist Form */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white py-20 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative container-narrow mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="accent" className="mb-6 mx-auto bg-accent-500/20 border border-accent-500/30 text-accent-500">
              Limited Availability
            </Badge>
            
            <h2 className="text-section-title mb-4 text-center">Join the Storm Pass waitlist</h2>
            <p className="text-lg text-snow-100 mb-10 text-center max-w-xl mx-auto">
              Be first to know when Storm Pass launches in your city. <strong className="text-white">Limited spots available.</strong>
            </p>

            {submitted ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                <Card variant="solid" className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent-500 flex items-center justify-center"
                  >
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h3 className="text-3xl font-black mb-3">You're on the list!</h3>
                  <p className="text-neutral-600 text-lg">
                    We'll email you as soon as Storm Pass is available in <strong className="text-brand-500">{city || 'your city'}</strong>.
                  </p>
                </Card>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full rounded-xl border-2 border-white/20 px-5 py-4 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-lg backdrop-blur-sm"
                  />
                </div>
                <div>
                  <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                    className="w-full rounded-xl border-2 border-white/20 px-5 py-4 bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-lg backdrop-blur-sm appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      backgroundSize: '1.5rem'
                    }}
                  >
                    <option value="" disabled className="bg-neutral-800 text-white">Select your city</option>
                    {canadianCities.map((city) => (
                      <option key={city.slug} value={city.slug} className="bg-neutral-800 text-white">
                        {city.name}, {city.provinceCode}
                      </option>
                    ))}
                  </select>
                </div>
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg" 
                  className="w-full bg-accent-500 hover:bg-accent-600 text-white border-0 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all text-lg font-black py-4"
                >
                  Join waitlist →
                </Button>
                
                <p className="text-center text-sm text-white/60 mt-4">
                  No spam. Unsubscribe anytime. We respect your inbox.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
